import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import {
  NgxDaterangepickerBootstrapComponent,
  NgxDaterangepickerBootstrapDirective,
  provideDaterangepickerLocale,
} from 'ngx-daterangepicker-bootstrap';

import * as Types from '../../shared/types';
import dayjs, { Dayjs } from 'dayjs';
import * as Constants from '../../shared/constant';
import { SharedDataService } from '../../services/shared-data.service';
import { CommonService } from '../../services/common.service';
import { ButtonComponent } from '../button/button.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { OutsideClickDirective } from '../../directives/outside-click.directive';

interface DateObject extends Object {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  label: string;
}

@Component({
  selector: 'filter-date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    NgxDaterangepickerBootstrapDirective,
    NgxDaterangepickerBootstrapComponent,
    ButtonComponent,
    TruncatePipe,
    OutsideClickDirective,
  ],
  providers: [
    {
      provide: provideDaterangepickerLocale,
      useFactory: () =>
        provideDaterangepickerLocale({
          separator: ' - ',
          applyLabel: 'Okay3',
        }),
    },
  ],
  templateUrl: './filter-date-range-picker.component.html',
  styleUrl: './filter-date-range-picker.component.scss',
})
export class FilterDaterangePickerComponent {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false }) pickerDirective1:
    | NgxDaterangepickerBootstrapDirective
    | undefined;
  @ViewChild('datePickerInput', { read: ElementRef, static: false }) datePickerInputRef: ElementRef<HTMLInputElement> | undefined;
  @Input() singleDatePicker: boolean = false;
  @Input() showFilterUI: boolean = false;
  @Input() customFormat: string = 'YYYY-MM-DD';
  /** When in filter UI, empty value from parent (e.g. after operator change) resets the picker so user selects again. */
  @Input() valueFromParent: string | string[] | null = null;
  /** When true, dropdown opens upward (e.g. for NOVA input at bottom of screen). */
  @Input() openUpward: boolean = false;
  @Output() onDateSelect = new EventEmitter<string | string[]>();

  id = Math.random();

  divWidthTop: string = '70px'; // Default width
  divWidthBottom: string = '65px'; // Default width
  protected _dropdownOpened = signal<boolean>(false);

  // DateRange Picker Start
  dropsDown = 'down';
  dropsUp = 'up';
  opensRight = 'right';
  opensCenter = 'center';
  opensLeft = 'left';
  selectedRangeCalendarAutoLeft: Types.SelectedRangeCalendar = {} as Types.SelectedRangeCalendar;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  invalidDates: Dayjs[] = [];
  startAPIDate: string | undefined;
  endAPIDate: string | undefined;
  selectedDate: string | undefined;
  referenceDate = dayjs().subtract(1, 'day');
  ranges = {
    'Last 7 days': [this.referenceDate.subtract(6, 'days').startOf('day'), this.referenceDate.endOf('day')],
    'This month': [this.referenceDate.startOf('month'), this.referenceDate.endOf('month')],
    'Last 3 months': [
      this.referenceDate.subtract(3, 'months').startOf('month'),
      this.referenceDate.subtract(1, 'month').endOf('month'),
    ]
    // ,
    // 'Last 6 months': [
    //   this.referenceDate.subtract(6, 'months').startOf('month'),
    //   this.referenceDate.subtract(1, 'month').endOf('month'),
    // ],
  } as any;
  localeTime = Constants.LocaleTime;
  locale = Constants.Locale;
  tooltips = [
    { date: dayjs(), text: '' },
    { date: dayjs().add(2, 'days'), text: '' },
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    public sharedDataService: SharedDataService,
    public commonService: CommonService
  ) {
    this.datepickerFunction();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.showFilterUI && changes['valueFromParent']) {
      const val = this.valueFromParent;
      const isEmpty = val === null || val === undefined || val === '' ||
        (Array.isArray(val) && val.length === 0);
      if (isEmpty) {
        this.resetFilterPickerSelection();
        return;
      }
      // Sync picker from parent value so we don't overwrite user's selection with today
      this.syncPickerFromParentValue(val);
      return;
    }
    this.datepickerFunction();
  }

  ngOnInit() {
    if (this.singleDatePicker || this.showFilterUI) {
      this.ranges = {};
    }
    // When filter UI has a value from parent, sync to it instead of resetting to today
    if (this.showFilterUI && this.valueFromParent != null && this.valueFromParent !== '' &&
      !(Array.isArray(this.valueFromParent) && this.valueFromParent.length === 0)) {
      this.syncPickerFromParentValue(this.valueFromParent);
    } else {
      this.datepickerFunction();
    }
  }

  ngOnDestroy() {
    this.pickerDirective1?.hide();
    this.cdr.detectChanges();
  }

  datepickerFunction() {
    if (this.singleDatePicker) {
      this.selectedRangeCalendarAutoLeft = {
        startDate: dayjs(),
        endDate: dayjs(),
      };
      this.divWidthBottom = '172px';
    }
    if (this.showFilterUI) {
      this.selectedRangeCalendarAutoLeft = {
        startDate: dayjs(),
        endDate: dayjs(),
      };
      this.divWidthBottom = '172px';
    } else {
      this.selectedRangeCalendarAutoLeft = {
        startDate: this.ranges['Last 7 days'][0],
        endDate: this.ranges['Last 7 days'][1],
        label: 'Last 7 Days',
      };
    }
  }

  isInvalidDate = (m: Dayjs) => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  isCustomDates = (date: Dayjs) => {
    return date.month() === 0 || date.month() === 6 ? 'mycustomdate' : false;
  };

  isTooltipDate = (m: Dayjs) => {
    const tooltip = this.tooltips.find((tt) => tt.date.isSame(m, 'day'));
    return tooltip ? tooltip.text : false;
  };

  toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
  };

  datesUpdatedRange($event: object) {
    const updatedRangeObject = $event as DateObject;

    if (this.singleDatePicker) {
      this.selectedDate = updatedRangeObject?.startDate?.format(this.customFormat);
      const apiDate = updatedRangeObject?.startDate?.format('YYYY-MM-DD');
      if (this.onDateSelect) {
        this.onDateSelect.emit(apiDate);
      }
    } else if (updatedRangeObject?.label) {
      this.selectedRangeCalendarAutoLeft.label = this.toTitleCase(updatedRangeObject.label);

      if (this.selectedRangeCalendarAutoLeft.label === 'Custom Range') {
        this.selectedRangeCalendarAutoLeft.label = `${this.selectedRangeCalendarAutoLeft.startDate.format('DD MMM YYYY')} - ${this.selectedRangeCalendarAutoLeft.endDate.format('DD MMM YYYY')}`;
      }

      if (this.selectedRangeCalendarAutoLeft.label.length < 12) {
        this.divWidthTop = '120px';
        this.divWidthBottom = '114px';
      } else if (this.selectedRangeCalendarAutoLeft.label.length < 15) {
        this.divWidthTop = '135px';
        this.divWidthBottom = '129px';
      } else {
        this.divWidthTop = '223px';
        this.divWidthBottom = '217px';
      }
      this.startAPIDate = this.selectedRangeCalendarAutoLeft.startDate.format('YYYY-MM-DD');
      this.endAPIDate = this.selectedRangeCalendarAutoLeft.endDate.format('YYYY-MM-DD');

      // Emit range for parents (e.g. NOVA) that need start/end without filter UI
      if (this.onDateSelect) {
        this.onDateSelect.emit([this.startAPIDate, this.endAPIDate]);
      }

      this.commonService.historyPageNumber = 1;
      this.commonService.setLoaderFlag(true);
    } else {
      // filter date range picker logic
      if (this.showFilterUI) {
        this.selectedDate = `${updatedRangeObject?.startDate?.format(this.customFormat)} - ${updatedRangeObject?.endDate?.format(this.customFormat)}`;
        const apiDate = [
          updatedRangeObject?.startDate?.format('YYYY-MM-DD'),
          updatedRangeObject?.endDate?.format('YYYY-MM-DD'),
        ];
        if (this.onDateSelect) {
          this.onDateSelect.emit(apiDate);
        }
      }
    }
  }

  makeLoaderTrue(event: MouseEvent) {
    this.commonService.setLoaderFlag(true);
    this.cdr.detectChanges();
  }
  // DateRange Picker End

  //load plots functions
  loadFetchFunctions() {
    this.cdr.detectChanges();
  }

  openDatepicker() {}

  toggleDropdown = () => {
    const picker = this.pickerDirective1;
    const inputEl = this.datePickerInputRef?.nativeElement;

    const openPicker = () => {
      if (picker) {
        picker.toggle();
      } else if (inputEl && this.openUpward) {
        // Fallback when ViewChild not ready (e.g. NOVA): trigger directive's host (click) -> open()
        inputEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }
      this.cdr.detectChanges();
    };

    // Defer so the directive and input are ready (fixes NOVA where picker was not opening)
    setTimeout(openPicker, 0);
  };

  handleOutSideClick = () => {
    this.pickerDirective1?.hide();
    this.cdr.detectChanges();
  };

  handleButtonClick = (event: MouseEvent) => {
    this.toggleDropdown();
    event?.stopPropagation();
  };

  /** When openUpward (NOVA), click on trigger row (but not on the button or input) opens the picker. */
  onTriggerClick = (event: MouseEvent) => {
    if (!this.openUpward) return;
    const target = event.target as Node;
    const inputEl = this.datePickerInputRef?.nativeElement;
    // Avoid double-toggle: ignore clicks on the input or on the button (button-wrapper)
    if (inputEl && (event.target === inputEl || inputEl.contains(target))) return;
    const host = this.elementRef.nativeElement as HTMLElement;
    const buttonWrapper = host?.querySelector?.('.button-wrapper');
    if (buttonWrapper?.contains(target)) return;
    this.toggleDropdown();
    event?.stopPropagation();
  };

  getDateLabel = () => {
    return this.selectedRangeCalendarAutoLeft.label ?? '';
  };

  /** Clears the selected date in filter UI when parent value is cleared (e.g. operator changed). */
  private resetFilterPickerSelection(): void {
    if (!this.showFilterUI) return;
    this.selectedDate = undefined;
    this.pickerDirective1?.clear();
    this.cdr.detectChanges();
  }

  /** Syncs picker model from parent value so the displayed selection is not overwritten by datepickerFunction (today). */
  private syncPickerFromParentValue(val: string | string[]): void {
    if (Array.isArray(val)) {
      const start = val[0] ? dayjs(val[0]) : dayjs();
      const end = val[1] ? dayjs(val[1]) : start;
      this.selectedRangeCalendarAutoLeft = { startDate: start, endDate: end };
      this.selectedDate = `${start.format(this.customFormat)} - ${end.format(this.customFormat)}`;
    } else {
      const d = val ? dayjs(val) : dayjs();
      this.selectedRangeCalendarAutoLeft = { startDate: d, endDate: d };
      this.selectedDate = d.format(this.customFormat);
    }
    this.cdr.detectChanges();
  }
}
